import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

function embedSrc(item) {
  if (item.provider === "youtube") {
    return `https://www.youtube.com/embed/${item.id}`;
  }
  if (item.provider === "vimeo") {
    return `https://player.vimeo.com/video/${item.id}`;
  }
  return null;
}

/**
 * Renders a single media item. Images that require authenticated fetch (org
 * post attachments) pass `resolveSrc`; everything else uses `item.url` (or
 * `item.directSrc`) directly.
 */
function MediaSlide({ item, resolveSrc, onStop }) {
  const needsResolve =
    !item.directSrc && !!resolveSrc && (item.type === "image" || item.type === "video");
  const [src, setSrc] = useState(item.directSrc || (needsResolve ? null : item.url || null));
  const [loading, setLoading] = useState(needsResolve);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setError(false);
    if (item.directSrc) {
      setSrc(item.directSrc);
      setLoading(false);
      return undefined;
    }
    if (needsResolve) {
      setLoading(true);
      resolveSrc(item)
        .then((url) => {
          if (cancelled) return;
          objectUrl = url;
          setSrc(url);
        })
        .catch(() => {
          if (!cancelled) setError(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      setSrc(item.url || null);
      setLoading(false);
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.url, item.type, resolveSrc, needsResolve]);

  if (item.type === "video_embed") {
    const iframeSrc = embedSrc(item);
    if (!iframeSrc) return null;
    return (
      <div className="relative w-full aspect-video overflow-hidden rounded-md bg-black">
        <iframe
          src={iframeSrc}
          title="Video"
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (item.type === "video") {
    if (loading) {
      return <div className="w-full h-56 rounded-md bg-muted/40 animate-pulse" />;
    }
    if (error || !src) {
      return (
        <div className="w-full h-40 rounded-md bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
          Couldn&apos;t load video
        </div>
      );
    }
    return (
      <video
        src={src}
        controls
        onClick={onStop}
        className="w-full max-h-[480px] rounded-md bg-black object-contain"
      />
    );
  }

  if (item.type === "gif") {
    const gifSrc = item.mp4_url || item.url;
    return item.mp4_url ? (
      <video
        src={gifSrc}
        poster={item.preview_url || undefined}
        autoPlay
        loop
        muted
        playsInline
        className="w-full max-h-[480px] rounded-md object-contain bg-muted/20"
        title={item.title || "GIF"}
      />
    ) : (
      <img
        src={gifSrc}
        alt={item.title || "GIF"}
        className="w-full max-h-[480px] rounded-md object-contain bg-muted/20"
        loading="lazy"
      />
    );
  }

  // image
  if (loading) {
    return (
      <div className="w-full h-56 rounded-md bg-muted/40 animate-pulse" />
    );
  }
  if (error || !src) {
    return (
      <div className="w-full h-40 rounded-md bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
        Couldn&apos;t load image
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={item.alt || "Attached image"}
      className="w-full max-h-[480px] rounded-md object-contain bg-muted/20"
      loading="lazy"
    />
  );
}

/**
 * Media carousel for post images/video/video_embed. Single item renders
 * without chrome; multiple items get prev/next + dot navigation.
 *
 * `resolveSrc(item) => Promise<string>` is used for images that require an
 * authenticated fetch (org post attachments); omit for public URLs.
 */
export default function MediaCarousel({ items = [], resolveSrc, className }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [list.length]);

  if (!list.length) return null;

  const clampedIndex = Math.min(index, list.length - 1);
  const current = list[clampedIndex];

  const goPrev = (e) => {
    e.stopPropagation();
    setIndex((i) => (i - 1 + list.length) % list.length);
  };
  const goNext = (e) => {
    e.stopPropagation();
    setIndex((i) => (i + 1) % list.length);
  };

  return (
    <div
      className={cn("relative", className)}
      onClick={(e) => e.stopPropagation()}
    >
      <MediaSlide
        item={current}
        resolveSrc={resolveSrc}
        onStop={(e) => e.stopPropagation()}
      />
      {list.length > 1 ? (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous media"
            className="absolute left-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/80 border border-border shadow-sm hover:bg-background"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next media"
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/80 border border-border shadow-sm hover:bg-background"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-background/70 px-2 py-1">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to media ${i + 1}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === clampedIndex
                    ? "w-4 bg-primary"
                    : "w-1.5 bg-muted-foreground/50"
                )}
              />
            ))}
          </div>
          <span className="absolute top-2 right-2 rounded-full bg-background/80 border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            {clampedIndex + 1}/{list.length}
          </span>
        </>
      ) : null}
    </div>
  );
}
